/**
 * Admin audit-log API layer (STORY-008-008). Typed calls to the SHIPPED
 * READ-only audit router (apps/api/garden/features/audit_log/router.py):
 *   GET /admin/audit-log            (keyset + filters + total_estimate)
 *   GET /admin/audit-log/export.csv (streamed CSV; self-audited server-side)
 *   GET /admin/audit-log/{id}       (single-row detail)
 * Adds NO backend routes. Types mirror audit_log/schemas.py. `ip` is masked
 * server-side (raw only for ops.admin_actions.read); the client treats it as
 * an opaque display string.
 */
import { apiClient } from "@/lib/api-client";

/** Acting user (nullable on SET NULL). Mirrors AuditActor. */
export interface AuditActor {
  id: string | null;
  email: string | null;
  display_name: string | null;
}

/** One audit row (mirrors AuditLogItem; ip already masked). */
export interface AuditLogItem {
  id: string;
  actor: AuditActor | null;
  actor_role: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  ip: string | null;
  user_agent: string | null;
  occurred_at: string;
}

/** Keyset page (mirrors AuditLogListResponse). */
export interface AuditLogListResponse {
  items: AuditLogItem[];
  next_cursor: string | null;
  total_estimate: number;
}

/** UI filter state. `actor` is a UUID or an email substring (server decides). */
export interface AuditFilters {
  actor: string | null;
  action: string | null;
  entityType: string | null;
  entityId: string | null;
  q: string | null;
  from: string | null; // ISO-8601 or "" (datetime-local value)
  to: string | null;
}

export const EMPTY_AUDIT_FILTERS: AuditFilters = {
  actor: null,
  action: null,
  entityType: null,
  entityId: null,
  q: null,
  from: null,
  to: null,
};

export const auditKeys = {
  list: (filters: AuditFilters) => ["admin", "audit-log", "list", filters] as const,
  detail: (id: string) => ["admin", "audit-log", "detail", id] as const,
} as const;

/** Build the shared filter query string (used by list + CSV export). */
function toSearchParams(f: AuditFilters): URLSearchParams {
  const q = new URLSearchParams();
  if (f.actor) q.set("actor", f.actor);
  if (f.action) q.set("action", f.action);
  if (f.entityType) q.set("entity_type", f.entityType);
  if (f.entityId) q.set("entity_id", f.entityId);
  if (f.q) q.set("q", f.q);
  if (f.from) q.set("from", f.from);
  if (f.to) q.set("to", f.to);
  return q;
}

export interface AuditListParams extends AuditFilters {
  cursor?: string | null;
  limit?: number;
}

export function listAuditLog(p: AuditListParams): Promise<AuditLogListResponse> {
  const q = toSearchParams(p);
  if (p.cursor) q.set("cursor", p.cursor);
  if (p.limit) q.set("limit", String(p.limit));
  const qs = q.toString();
  return apiClient.get<AuditLogListResponse>(
    `/admin/audit-log${qs ? `?${qs}` : ""}`,
  );
}

export function getAuditRow(id: string): Promise<AuditLogItem> {
  return apiClient.get<AuditLogItem>(`/admin/audit-log/${id}`);
}

/**
 * Relative path for the streamed CSV. The JWT lives in JS memory (never a
 * cookie), so a native anchor navigation cannot authenticate this request.
 * The export button therefore fetches this path with an explicit
 * `Authorization: Bearer <token>` header and streams the blob to a download
 * (see AuditLogPage.handleExport). This helper only builds the query string.
 */
export function auditExportCsvUrl(f: AuditFilters): string {
  const qs = toSearchParams(f).toString();
  return `/admin/audit-log/export.csv${qs ? `?${qs}` : ""}`;
}
